import { Injectable } from '@angular/core';
import { AppState } from '@cpd-app/state';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';

import * as AuthActions from './auth.actions';
import * as AuthSelectors from './auth.selectors';

@Injectable({
  providedIn: 'root'
})
export class AuthFacade {
  public readonly isCouncelor$ = this.store.pipe(
    select(AuthSelectors.selectIsCouncelor)
  );
  public readonly isCounselorOrAbove$ = this.store.pipe(
    select(AuthSelectors.selectIsCounselorOrAbove)
  );
  /** STAFF-3. Header display and the shared report band only; see the selector. */
  public readonly isStaffSignedIn$ = this.store.pipe(
    select(AuthSelectors.selectIsStaffSignedIn)
  );
  // True once getSession has answered. Admin routes wait on this so a guard
  // cannot decide authorization before the session has been restored.
  public readonly isSessionLoaded$ = this.store.pipe(
    select(AuthSelectors.selectIsSessionLoaded)
  );
  public readonly isAdmin$ = this.store.pipe(
    select(AuthSelectors.selectIsAdmin)
  );
  public readonly isCounselorAdmin$ = this.store.pipe(
    select(AuthSelectors.selectIsCounselorAdmin)
  );
  public readonly isSuperAdmin$ = this.store.pipe(
    select(AuthSelectors.selectIsSuperAdmin)
  );

  public readonly loginWallRedirectUrl$ = this.store.pipe(
    select(AuthSelectors.selectLoginWallRedirectUrl)
  );
  public readonly errorMessage$ = this.store.pipe(
    select(AuthSelectors.selectErrorMessage)
  );

  /**
   * The bearer token, as the session interceptor reads it.
   *
   * Worth knowing before reaching for `isSessionInitializing$` instead: "the
   * session has initialised" and "a request will carry an Authorization header"
   * are not the same moment. `SessionInterceptor.parseHeader` attaches the
   * header only once this is set, and the session flag clears first, so a
   * permission-gated query gated on the flag can still go out unauthenticated
   * and come back Forbidden as a normal 200.
   */
  public readonly authToken$ = this.store.pipe(
    select(AuthSelectors.selectAuthToken)
  );

  public readonly shareToken$ = this.store.pipe(
    select(AuthSelectors.selectAuthSharedToken)
  );

  public readonly returnSessionResult$ = this.store.pipe(
    select(AuthSelectors.selectReturnSessionResult)
  );

  public readonly isUserAuthenticated$ = this.store.pipe(
    select(AuthSelectors.selectIsAuthenticated)
  );

  public readonly isOverPasscode$ = this.store.pipe(
    select(AuthSelectors.selectIsOverPasscode)
  );

  public readonly currentUser$ = this.store.pipe(
    select(AuthSelectors.selectCurrentUser)
  );

  public readonly getSessionSuccessType = AuthActions.getSessionSuccess.type;

  public readonly username$ = this.store.pipe(
    select(AuthSelectors.selectUsername)
  );

  public readonly firstName$ = this.store.pipe(
    select(AuthSelectors.selectFirstName)
  );

  public readonly authProfile$ = this.store.pipe(
    select(AuthSelectors.selectProfile)
  );

  public readonly isSessionInitializing$ = this.store.pipe(
    select(AuthSelectors.selectIsSessionInitializing)
  );

  public readonly logInFailure$ = this.actions$.pipe(
    ofType(AuthActions.adminLogInFailure)
  );

  /**
   * AD-1. The counselor sign-in dialog stayed open over the dashboard it had
   * just navigated to.
   *
   * The dialog subscribed to logInFailure$ and to nothing for success, because
   * this facade exposed no success observable at all -- so nothing ever told it
   * to close. The router navigates underneath while the CDK overlay, which is
   * independent of the router outlet, simply stays put.
   *
   * Mirrors logInFailure$ exactly, so the dialog's two subscriptions read the
   * same way.
   */
  public readonly logInSuccess$ = this.actions$.pipe(
    ofType(AuthActions.adminLogInSuccess)
  );

  public readonly submitPasscodeFailure$ = this.actions$.pipe(
    ofType(AuthActions.submitPasscodeFailure)
  );
  public readonly getSessionSuccess$ = this.actions$.pipe(
    ofType(AuthActions.getSessionSuccess)
  );

  constructor(
    private store: Store<AppState>,
    private actions$: Actions
  ) {}

  public initAuthState(): void {
    this.store.dispatch(AuthActions.initAuthState());
  }

  public logIn(username: string, password: string): void {
    this.store.dispatch(AuthActions.adminLogIn({ username, password }));
  }

  public submitPasscode(passcode: string): void {
    this.store.dispatch(AuthActions.submitPasscode({ passcode }));
  }

  public logOut(): void {
    this.store.dispatch(AuthActions.adminLogOut());
  }

  public setLoginWallRedirectUrl(loginWallRedirectUrl: string): void {
    this.store.dispatch(
      AuthActions.setLoginWallRedirectUrl({ loginWallRedirectUrl })
    );
  }

  public createReturnSession(emailAddress: string): void {
    this.store.dispatch(AuthActions.createReturnSession({ emailAddress }));
  }

  public setTokenToStorage(token: string, refreshToken?: string): void {
    this.store.dispatch(
      AuthActions.saveTokenToStorage({ token, refreshToken })
    );
    this.store.dispatch(AuthActions.saveTokenToState({ token, refreshToken }));
  }

  public clearShareToken(): void {
    this.store.dispatch(AuthActions.clearShareToken());
  }
}
